import { generatePath } from 'react-router';

import {
  AbilityBuilder,
  AnyAbility,
  createAliasResolver,
  createMongoAbility,
} from '@casl/ability';

import {
  Action,
  Permission,
  Resource,
  type SurfaceCoreApiEntitiesUserRole,
  SystemRoleType,
  type UserSession,
} from '@/api/client';
import {
  getCaslOverrideMap,
  getRoleBaselineOverride,
  getTenantFeatureOverrideMap,
  shouldApplyPermissionOverridesForSession,
} from '@/lib/auth/permission-overrides-storage';
import {
  getTemplateNameFromBaseline,
  isDefaultRoleBaseline,
  isTemplateRoleBaseline,
  resolveSessionRolesFromBaseline,
} from '@/lib/auth/role-baseline-utils';
import { ROLE_PERMISSION_TEMPLATES } from '@/lib/auth/role-permission-templates';
import * as paths from '@/router/paths';

/** Licensed product keys on `UserSession.enabled_features` (sync with backend session factory). */
export const TENANT_FEATURE_KEYS = [
  'lease_audits',
  'due_diligence',
  'past_due_diligence',
  'transitions',
] as const;

export type TenantFeatureKey = (typeof TENANT_FEATURE_KEYS)[number];

const CASL_RESOLVE_ACTION = {
  resolveAction: createAliasResolver({
    [Action.Full]: [Action.Read, Action.Create, Action.Update, Action.Delete],
  }),
};

/** Stable localStorage / URL key for a CASL override entry. */
export const makeCaslOverrideKey = (
  resource: Resource,
  action: Action
): string => `${resource}:${action}`;

export const parseCaslOverrideKey = (
  key: string
): { resource: Resource; action: Action } | null => {
  const colonIdx = key.indexOf(':');
  if (colonIdx === -1) return null;
  const resource = key.slice(0, colonIdx) as Resource;
  const action = key.slice(colonIdx + 1) as Action;
  return { resource, action };
};

/** Role list from the user session (`/me`); each role carries `permissions` used to build CASL rules. */
export type UserRoles = UserSession['roles'];

/**
 * Builds a CASL {@link AnyAbility} from API role permissions.
 *
 * Flattens every role’s `permissions` into Mongo-style rules (`subject` = resource, `action`).
 * **`Action.Full`** is expanded so a single Full grant implies read, create, update, and delete
 * on that resource.
 *
 * @param roles - Session roles (or empty); `null`/`undefined` yields an ability that denies everything.
 * @returns CASL ability instance for use with `ability.can(action, resource)` and route/UI guards.
 */
export function createAbilityFromRoles(
  roles: UserRoles | null | undefined
): AnyAbility {
  return createMongoAbility(
    (roles || []).flatMap((role: SurfaceCoreApiEntitiesUserRole) =>
      (role.permissions || []).map(({ resource, action }) => ({
        subject: resource,
        action,
      }))
    ),
    CASL_RESOLVE_ACTION
  );
}

/**
 * Roles that apply when acting on a specific property.
 *
 * Mirrors the backend's `User.get_roles(saas_tenant_id, property_id)`: a role with
 * no `property_id` is tenant-wide and always applies, a property-scoped role only
 * applies to its own property. Passing `undefined`/`null` for `propertyId` means
 * "no property context" and returns every role (the tenant-wide union).
 *
 * Needed because a session's roles can differ per property — e.g. a VP over a
 * handful of properties who is a Leasing Agent elsewhere. Flattening every role
 * into one ability shows actions the API will reject for the property in view.
 */
export function rolesForProperty(
  roles: UserRoles | null | undefined,
  propertyId: string | null | undefined
): UserRoles {
  const list = roles ?? [];
  if (!propertyId) return list;
  return list.filter(
    (role) => !role.property_id || role.property_id === propertyId
  );
}

/**
 * Deduped permission pairs from session roles (for debug UI / discovery).
 */
export function getSessionPermissions(
  roles: UserRoles | null | undefined
): Permission[] {
  const seen = new Set<string>();
  const permissions: Permission[] = [];

  for (const role of roles ?? []) {
    for (const permission of role.permissions ?? []) {
      const key = makeCaslOverrideKey(permission.resource, permission.action);
      if (seen.has(key)) continue;
      seen.add(key);
      permissions.push(permission);
    }
  }

  return permissions;
}

/**
 * Target roles for a stored role-baseline selection (session subset or template).
 */
export function resolveTargetRolesFromBaseline(
  session: UserSession,
  baseline: string = getRoleBaselineOverride()
): UserRoles {
  const sessionRoles = session.roles ?? [];

  if (isTemplateRoleBaseline(baseline)) {
    const templateName = getTemplateNameFromBaseline(baseline);
    const template = templateName
      ? ROLE_PERMISSION_TEMPLATES[templateName]
      : undefined;
    if (!template) {
      return sessionRoles;
    }
    return [
      {
        role: templateName!,
        user_id: session.id,
        saas_tenant_id: session.saas_tenant_id,
        label: template.label,
        system_type: template.systemType,
        permissions: template.permissions,
      },
    ];
  }

  return resolveSessionRolesFromBaseline(baseline, sessionRoles);
}

/**
 * CASL overrides that make effective UI permissions match the selected role simulation.
 * Compared against the real session from `/me`; only emitted where server ≠ target.
 */
export function computeRoleSimulationCaslOverrides(
  session: UserSession
): Record<string, boolean> {
  if (!shouldApplyPermissionOverridesForSession(session)) return {};

  const baseline = getRoleBaselineOverride();
  const sessionRoles = session.roles ?? [];
  if (isDefaultRoleBaseline(baseline, sessionRoles.length)) return {};

  const targetRoles = resolveTargetRolesFromBaseline(session, baseline);
  const overrides: Record<string, boolean> = {};

  for (const resource of Object.values(Resource)) {
    for (const action of Object.values(Action)) {
      const serverGranted = sessionHasPermission(
        sessionRoles,
        resource,
        action
      );
      const targetGranted = sessionHasPermission(targetRoles, resource, action);
      if (serverGranted !== targetGranted) {
        overrides[makeCaslOverrideKey(resource, action)] = targetGranted;
      }
    }
  }

  return overrides;
}

/** Role-simulation overrides merged with manual staff overrides (manual wins). */
export function getEffectiveCaslOverrideMap(
  session: UserSession
): Record<string, boolean> {
  if (!shouldApplyPermissionOverridesForSession(session)) return {};
  return {
    ...computeRoleSimulationCaslOverrides(session),
    ...getCaslOverrideMap(),
  };
}

/** Permissions to list in the debug drawer (server + simulation target + manual overrides). */
export function getPermissionDiscoveryPermissions(
  session: UserSession
): Permission[] {
  const sessionRoles = session.roles ?? [];
  const seen = new Set<string>();
  const permissions: Permission[] = [];

  const addFromRoles = (roles: UserRoles | null | undefined) => {
    for (const permission of getSessionPermissions(roles)) {
      const key = makeCaslOverrideKey(permission.resource, permission.action);
      if (seen.has(key)) continue;
      seen.add(key);
      permissions.push(permission);
    }
  };

  addFromRoles(sessionRoles);

  if (
    shouldApplyPermissionOverridesForSession(session) &&
    !isDefaultRoleBaseline(getRoleBaselineOverride(), sessionRoles.length)
  ) {
    addFromRoles(resolveTargetRolesFromBaseline(session));
  }

  for (const key of Object.keys(getEffectiveCaslOverrideMap(session))) {
    const parsed = parseCaslOverrideKey(key);
    if (!parsed) continue;
    const dedupeKey = makeCaslOverrideKey(parsed.resource, parsed.action);
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    permissions.push({
      resource: parsed.resource,
      action: parsed.action,
    });
  }

  return permissions;
}

/**
 * Whether the raw session grants an action on a resource (respects {@link Action.Full} aliases).
 */
export function sessionHasPermission(
  roles: UserRoles | null | undefined,
  resource: Resource,
  action: Action
): boolean {
  return createAbilityFromRoles(roles).can(action, resource);
}

/**
 * Applies staff CASL grant/deny overrides on top of a session-derived ability.
 */
export function applyCaslOverrides(
  baseAbility: AnyAbility,
  overrideMap: Record<string, boolean>
): AnyAbility {
  const builder = new AbilityBuilder(createMongoAbility);

  for (const rule of baseAbility.rules) {
    const subject = rule.subject as string;
    const action = rule.action as string;
    if (rule.inverted) {
      builder.cannot(action, subject);
    } else {
      builder.can(action, subject);
    }
  }

  for (const [key, granted] of Object.entries(overrideMap)) {
    const parsed = parseCaslOverrideKey(key);
    if (!parsed) continue;
    if (granted) {
      builder.can(parsed.action, parsed.resource);
    } else {
      builder.cannot(parsed.action, parsed.resource);
    }
  }

  return builder.build(CASL_RESOLVE_ACTION);
}

/**
 * Session roles → CASL ability, optionally merged with staff debug overrides.
 */
export function createEffectiveAbilityFromRoles(
  session: UserSession,
  roles: UserRoles | null | undefined,
  overrideMap: Record<string, boolean> = getCaslOverrideMap()
): AnyAbility {
  const base = createAbilityFromRoles(roles);
  if (!shouldApplyPermissionOverridesForSession(session)) return base;
  if (Object.keys(overrideMap).length === 0) return base;
  return applyCaslOverrides(base, overrideMap);
}

/**
 * Merges tenant-feature staff overrides into the session `enabled_features` list.
 */
export function applyTenantFeatureOverrides(session: UserSession): string[] {
  const base = [...(session.enabled_features ?? [])];
  if (!shouldApplyPermissionOverridesForSession(session)) return base;

  const overrides = getTenantFeatureOverrideMap();
  const featureSet = new Set(base);

  for (const [featureKey, enabled] of Object.entries(overrides)) {
    if (enabled) {
      featureSet.add(featureKey);
    } else {
      featureSet.delete(featureKey);
    }
  }

  return [...featureSet];
}

/**
 * Returns a session copy with effective `enabled_features` (roles stay raw from API).
 */
export function applySessionPermissionOverrides(
  session: UserSession
): UserSession {
  return {
    ...session,
    enabled_features: applyTenantFeatureOverrides(session),
  };
}

/**
 * Roles used for UI presentation checks (e.g. Transitions guest chrome).
 *
 * When staff permission overrides are applying and a non-default role baseline is
 * selected, returns the simulated target roles. Otherwise returns session roles.
 * Does not mutate `user.roles` on the session — keep server assignments for debug
 * “Server” columns, analytics, and admin UIs.
 */
export function resolveEffectiveRoles(session: UserSession): UserRoles {
  const sessionRoles = session.roles ?? [];
  if (!shouldApplyPermissionOverridesForSession(session)) {
    return sessionRoles;
  }
  const baseline = getRoleBaselineOverride();
  if (isDefaultRoleBaseline(baseline, sessionRoles.length)) {
    return sessionRoles;
  }
  return resolveTargetRolesFromBaseline(session, baseline);
}

/**
 * Whether effective (or raw) roles include a given system role type.
 * Matches either `system_type` or `role` string against {@link SystemRoleType}.
 */
export function sessionHasSystemRole(
  roles: UserRoles | null | undefined,
  systemType: SystemRoleType
): boolean {
  return Boolean(
    roles?.some(
      (role) => role.system_type === systemType || role.role === systemType
    )
  );
}

export type EffectiveAuth = {
  /** Session with effective tenant features; `roles` remain server truth. */
  user: UserSession;
  /** Override-aware roles for presentation checks. */
  roles: UserRoles;
  ability: AnyAbility;
};

/**
 * Single entry point for effective UI auth: patched tenant features, effective
 * roles for presentation, and CASL ability. Used by {@link AuthProvider} and
 * non-React loaders.
 */
export function resolveEffectiveAuth(session: UserSession): EffectiveAuth {
  return {
    user: applySessionPermissionOverrides(session),
    roles: resolveEffectiveRoles(session),
    ability: createEffectiveAbilityFromRoles(
      session,
      session.roles,
      getEffectiveCaslOverrideMap(session)
    ),
  };
}

/**
 * Resources that, if the user can **read** any one of them, unlock the Ask experience.
 *
 * Kept in sync with backend / Ask pipeline expectations. Used by {@link canAskFromAbility}
 * and route/sidebar gating for Ask routes.
 */
export const askResources = [
  Resource.Applications,
  Resource.Residents,
  Resource.Units,
  Resource.Policies,
  Resource.Leases,
  Resource.MoveIns,
  Resource.MoveOuts,
  Resource.Renewals,
  Resource.WorkOrders,
  Resource.Sales,
  Resource.LeaseAudits,
] as const;

/**
 * Whether the user may access Ask (read on at least one {@link askResources} resource).
 *
 * @param ability - Typically from {@link createAbilityFromRoles} or `AbilityProvider`.
 * @returns `true` if `ability.can(Action.Read, resource)` for any entry in {@link askResources}.
 */
export function canAskFromAbility(ability: AnyAbility): boolean {
  return askResources.some((resource) => ability.can(Action.Read, resource));
}

/** Options for {@link getDefaultLandingPath} that are not inferable from CASL alone. */
export interface DefaultLandingPathOptions {
  /** Session flag; required for document-management landing when feature is on. */
  isAdmin?: boolean;
  /** Amplitude feature flag; document management is only a candidate when true. */
  documentManagementEnabled?: boolean;
  /**
   * Active tenant URL slug. Required to materialise tenant-prefixed landing
   * paths (e.g. `/~/:tenantSlug/lease-audits`). When omitted (caller is on a
   * global page), the route definition resolves `:tenantSlug` from the URL via
   * the `paths.ts` runtime fallback.
   */
  tenantSlug?: string;
  /**
   * Tenant-licensed features from `UserSession.enabled_features`. Required to
   * gate product areas that check feature enablement in addition to permissions
   * (e.g. lease_audits, due_diligence, transitions).
   */
  enabledFeatures?: string[];
}

/**
 * Resolves the first app route the user should land on: after login, on `/`, or when
 * `ProtectedRoute` denies access.
 *
 * Evaluated in **priority order** (first match wins):
 * 1. Lease audits → {@link paths.LEASE_AUDITS} (requires `lease_audits` feature + ability)
 * 2. Ask-eligible → {@link paths.ASK} ({@link canAskFromAbility})
 * 3. Due diligence → {@link paths.DUE_DILIGENCES} (requires `due_diligence`/`past_due_diligence` feature + ability)
 * 4. Documents (leases) → {@link paths.DOCUMENTS}
 * 5. Reported audits → {@link paths.ISSUES_REPORTED}
 * 6. Document management → {@link paths.DOCUMENT_MANAGEMENT} (only if `isAdmin` and `documentManagementEnabled`)
 * 7. Fallback → {@link paths.HELP} (no matching permissions; avoids redirect loop on `/`)
 *
 * @param ability - Session-derived CASL ability.
 * @param options - Admin and feature-flag hints for non-permission landing targets.
 * @returns Pathname string suitable for `Navigate` or loader redirect.
 */
export function getDefaultLandingPath(
  ability: AnyAbility,
  options: DefaultLandingPathOptions = {}
): string {
  const {
    isAdmin = false,
    documentManagementEnabled = false,
    tenantSlug,
    enabledFeatures = [],
  } = options;

  // Pick the route template (still contains `:tenantSlug`) by ability, then
  // materialise it with the resolved tenant slug below.
  const template = ((): string => {
    if (
      ability.can(Action.Read, Resource.LeaseAudits) &&
      enabledFeatures.includes('lease_audits')
    ) {
      return paths.LEASE_AUDITS;
    }
    if (canAskFromAbility(ability)) {
      return paths.ASK;
    }
    if (
      ability.can(Action.Read, Resource.DueDiligences) &&
      (enabledFeatures.includes('due_diligence') ||
        enabledFeatures.includes('past_due_diligence'))
    ) {
      return paths.DUE_DILIGENCES;
    }
    if (ability.can(Action.Read, Resource.Leases)) {
      return paths.DOCUMENTS;
    }
    if (ability.can(Action.Read, Resource.LeaseAuditAIIssueRequests)) {
      return paths.ISSUES_REPORTED;
    }
    if (isAdmin && documentManagementEnabled) {
      return paths.DOCUMENT_MANAGEMENT;
    }
    return paths.HELP;
  })();

  const urlTenantSlug = paths.readTenantSlugFromCurrentUrl();
  const routeTenantSlug = tenantSlug ?? urlTenantSlug;

  if (!routeTenantSlug) {
    // Without a tenant slug we cannot fully resolve a tenant-prefixed URL.
    // Fall back to the bare root and let `defaultLandingLoader` retry once
    // it has fetched `/api/me`.
    return '/';
  }

  return generatePath(template, { tenantSlug: routeTenantSlug });
}
